import type {GridPoint, ImagePoint, LevelGeometryDocument, MapRegistration, WorldPoint} from "@/dungeons/model/DungeonGeometry.ts";

export const PIXELS_PER_METER = 100;

const round = (value: number) => Math.round(value * 10_000) / 10_000;

export const imageToGrid = (point: ImagePoint, registration: LevelGeometryDocument["registration"]): GridPoint => ({
    x: round((point.x - registration.gridOffsetX) / registration.pixelsPerCell),
    y: round((point.y - registration.gridOffsetY) / registration.pixelsPerCell),
});

export const gridToImage = (point: GridPoint, registration: LevelGeometryDocument["registration"]): ImagePoint => ({
    x: round(point.x * registration.pixelsPerCell + registration.gridOffsetX),
    y: round(point.y * registration.pixelsPerCell + registration.gridOffsetY),
});

export const gridToWorld = (point: GridPoint, grid: LevelGeometryDocument["grid"]): WorldPoint => ({
    x: round(point.x * grid.metersPerCell * PIXELS_PER_METER),
    y: round(point.y * grid.metersPerCell * PIXELS_PER_METER),
});

export const worldToGrid = (point: WorldPoint, grid: LevelGeometryDocument["grid"]): GridPoint => ({
    x: round(point.x / (grid.metersPerCell * PIXELS_PER_METER)),
    y: round(point.y / (grid.metersPerCell * PIXELS_PER_METER)),
});

export const snapToGrid = (point: GridPoint): GridPoint => ({x: Math.round(point.x), y: Math.round(point.y)});
export const snapImagePointToGrid = (
    point: ImagePoint,
    registration: LevelGeometryDocument["registration"],
): ImagePoint => gridToImage(snapToGrid(imageToGrid(point, registration)), registration);

export const snapToHalfGrid = (point: GridPoint): GridPoint => ({
    x: Math.round(point.x * 2) / 2,
    y: Math.round(point.y * 2) / 2,
});

export const mapImageTransform = (geometry: LevelGeometryDocument) => {
    const scale = geometry.grid.metersPerCell * PIXELS_PER_METER / geometry.registration.pixelsPerCell;
    return {
        x: -geometry.registration.gridOffsetX * scale,
        y: -geometry.registration.gridOffsetY * scale,
        scaleX: scale,
        scaleY: scale,
    };
};

/** Convert without rounding so projecting between levels does not accumulate drift. */
export const imageToWorld = (point: ImagePoint, geometry: LevelGeometryDocument): WorldPoint => {
    const transform = mapImageTransform(geometry);
    return {x: transform.x + point.x * transform.scaleX, y: transform.y + point.y * transform.scaleY};
};

export const worldToImage = (point: WorldPoint, geometry: LevelGeometryDocument): ImagePoint => {
    const transform = mapImageTransform(geometry);
    return {x: (point.x - transform.x) / transform.scaleX, y: (point.y - transform.y) / transform.scaleY};
};

export const recalibrateGeometry = (
    geometry: LevelGeometryDocument,
    registration: MapRegistration,
    metersPerCell = geometry.grid.metersPerCell,
): LevelGeometryDocument => {
    const transform = (point: GridPoint) => imageToGrid(gridToImage(point, geometry.registration), registration);
    return {
        ...geometry,
        grid: {...geometry.grid, metersPerCell},
        registration,
        walls: geometry.walls.map(wall => ({...wall, points: wall.points.map(transform)})),
        doors: geometry.doors.map(door => ({...door, start: transform(door.start), end: transform(door.end)})),
        transitions: geometry.transitions.map(transition => ({
            ...transition, points: transition.points.map(transform),
        })),
        annotations: geometry.annotations.map(annotation => ({...annotation, position: transform(annotation.position)})),
        lights: geometry.lights.map(light => ({...light, position: transform(light.position)})),
    };
};

export const setGridOrigin = (geometry: LevelGeometryDocument, imagePoint: ImagePoint): LevelGeometryDocument => {
    const origin = snapImagePointToGrid(imagePoint, geometry.registration);
    return recalibrateGeometry(geometry, {
        ...geometry.registration,
        gridOffsetX: origin.x,
        gridOffsetY: origin.y,
    });
};
